/**
 * AutoresponderActivity.tsx
 *
 * The newest automatic replies for this practice (R21, KD5): sent, held back,
 * failed and unconfirmed, for patients and leads alike. Every stored code is
 * shown as words from autoresponderForm; the recipient arrives masked.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { formatPacificDateTime, resolveDisplayTimezone } from '@/lib/facilityTimezone';
import * as autorespondersSvc from '@/services/autoresponders.service';
import type { AutoresponderActivityRow, AutoresponderRule } from '@/services/autoresponders.service';
import {
  CHANNEL_LABELS,
  COPY,
  STATUS_BADGE,
  STATUS_LABELS,
  activityReason,
  displayStatus,
  errorMessageFromError,
} from './autoresponderForm';

const ALL_RULES = 0;

interface Props {
  rules: AutoresponderRule[];
}

export function AutoresponderActivity({ rules }: Props) {
  const [rows, setRows] = useState<AutoresponderActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ruleId, setRuleId] = useState<number>(ALL_RULES);
  // Fixed per load, so "sending" vs "interrupted" does not flip between renders.
  const [loadedAt, setLoadedAt] = useState(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await autorespondersSvc.listAutoresponderActivity(ruleId === ALL_RULES ? null : ruleId);
      setRows(data);
      setLoadedAt(Date.now());
      setError(null);
    } catch (err) {
      setError(errorMessageFromError(err, COPY.activityError));
    } finally {
      setLoading(false);
    }
  }, [ruleId]);

  useEffect(() => {
    void load();
  }, [load]);

  const timezone = useMemo(() => resolveDisplayTimezone(), []);
  const windowByRule = useMemo(
    () => new Map(rules.map((r) => [r.id, r.repeatWindowHours])),
    [rules],
  );

  const ruleOptions = useMemo(
    () => [
      { value: ALL_RULES, label: COPY.activityAllRules },
      ...rules.map((r) => ({ value: r.id, label: r.name })),
    ],
    [rules],
  );

  const columns: DataTableColumn<AutoresponderActivityRow>[] = [
    {
      key: 'createdAt',
      header: COPY.activityColumnTime,
      render: (row) => (
        <span className="whitespace-nowrap text-sm text-slate-700">
          {formatPacificDateTime(row.createdAt, timezone)}
        </span>
      ),
    },
    {
      key: 'ruleName',
      header: COPY.activityColumnRule,
      render: (row) => (
        <span className="text-sm text-slate-900">
          {row.ruleName ?? COPY.activityNoRule}
          {row.ruleDeleted && <span className="ml-1 text-slate-400">{COPY.activityDeletedRule}</span>}
        </span>
      ),
    },
    {
      key: 'channel',
      header: COPY.activityColumnChannel,
      render: (row) => (
        <span className="text-sm text-slate-700">{CHANNEL_LABELS[row.channel] ?? ''}</span>
      ),
    },
    {
      key: 'recipient',
      header: COPY.activityColumnRecipient,
      render: (row) => <span className="text-sm text-slate-700">{row.recipient || '—'}</span>,
    },
    {
      key: 'status',
      header: COPY.activityColumnResult,
      render: (row) => {
        const status = displayStatus(row, loadedAt);
        return <Badge variant={STATUS_BADGE[status]}>{STATUS_LABELS[status]}</Badge>;
      },
    },
    {
      key: 'reason',
      header: COPY.activityColumnReason,
      render: (row) => (
        <span className="text-sm text-slate-600">
          {activityReason(row, loadedAt, {
            repeatWindowHours: row.ruleId !== null ? windowByRule.get(row.ruleId) : null,
          })}
        </span>
      ),
    },
  ];

  return (
    <div className="flex-1 min-h-0 flex flex-col pt-4">
      <div className="flex items-center gap-3 mb-4 flex-shrink-0">
        <div className="w-full max-w-xs">
          <Select<number>
            id="autoresponder-activity-rule"
            label={COPY.activityRuleFilterLabel}
            options={ruleOptions}
            value={ruleId}
            onChange={(v) => setRuleId(v ?? ALL_RULES)}
          />
        </div>
      </div>

      {error && (
        <p className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="flex-1 min-h-0">
        <DataTable<AutoresponderActivityRow>
          data={rows}
          columns={columns}
          keyField="id"
          loading={loading}
          emptyTitle={COPY.activityEmpty}
        />
      </div>
    </div>
  );
}
